import { useContext, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { getShiftRulesData, updateShiftRequest, updateAllRulesShiftRequest } from '@vyce/core/src/api/time';
import { TimeModuleContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useBooleanState } from '../../../../hooks';
import { EnumSchema, Props, RuleSchema } from '../types';
import { Shift, SiteShiftRule, TimeAndAttendanceLocation } from '../../../../types';
import { SettingsOptions } from '../../../../api/types';
import { shiftToSiteShift } from '../../../../modules/timeModule/utils';
import { getItemsForEnum } from '../helpers/getItemsForEnum';
import { mapperRulesNames } from '../config/constants';

/**
 * Поля, которые рендерит UI, но которых может ещё не быть в схеме с бэкенда.
 * Без дефолта такое поле не попадёт ни в форму, ни в запрос на сохранение.
 */
const EXTRA_FIELD_DEFAULTS: Record<string, Record<string, string>> = {
  [mapperRulesNames.FULL_WORK_DAY_BONUSES]: {
    bonus_minutes: '0',
  },
};

const transformRuleSchema = (settings: SettingsOptions) => {
  const schema = Object.keys(settings).reduce<Record<string, any> & { uuid: string; name: string }>(
    (sum, curr) => {
      if (['uuid', 'name'].includes(curr)) {
        sum[curr] = settings[curr];
        return sum;
      }

      if (curr === 'active') {
        sum[curr] = settings[curr].default === 'true';
        return sum;
      }
      if (settings[curr].type === 'enum') {
        // @ts-ignore
        sum[curr] = { default: settings[curr].default, options: getItemsForEnum(settings[curr].options) };

        return sum;
      }

      sum[curr] = settings[curr].default;
      return sum;
    },
    {} as RuleSchema
  );

  Object.entries(EXTRA_FIELD_DEFAULTS[schema.name] ?? {}).forEach(([field, defaultValue]) => {
    if (!(field in schema)) {
      schema[field] = defaultValue;
    }
  });

  return schema;
};

export const useData = ({ companyId }: Props) => {
  const [shifts, setShifts] = useState<Shift[]>();
  const [shiftRuleSchemas, setShiftRuleSchemas] = useState<RuleSchema[]>();
  const [isLoading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const { handleServerError } = useContext(NotificationContext);
  const { locations, getLocations } = useContext(TimeModuleContext);
  const methods = useForm<
    {
      location: TimeAndAttendanceLocation;
      shift?: Shift;
    } & Record<string, any>
  >({
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const location = methods.watch('location');
  const shift = methods.watch('shift');
  const isButtonDisabled = !location || !shift;

  const getShiftRules = async () => {
    if (!companyId) {
      return;
    }
    try {
      setLoadingTrue();
      const { data } = await getShiftRulesData({
        companyId,
        payload: {
          offset: 0,
          limit: 10,
          order_by: [
            {
              field: 'updated_at',
              desc: true,
            },
          ],
        },
      });

      setShiftRuleSchemas(data.items.map(item => transformRuleSchema(item.settings)));

      setLoadingFalse();
    } catch (e) {
      setLoadingFalse();
      handleServerError(e);
    }
  };

  const prepareRulesData = (): (Record<string, any> & {
    id: string;
    name: string;
    active: boolean;
  })[] => {
    const values = methods.getValues();
    const res = selectedRules
      .map(id => {
        const {
          name = '',
          uuid = '',
          ...restValues
        } = shiftRuleSchemas?.find(rule => rule.uuid === id) ?? {};

        const res: Record<string, any> & { id: string; name: string; active: boolean } = {
          id: uuid,
          name: name,
          active: selectedRules.includes(uuid),
        };

        // Поля берём из схемы и из формы: поле, которое рендерит UI, но которого ещё нет
        // в схеме бэкенда (например bonus_minutes), иначе молча не попадёт в запрос
        const fieldPrefix = `${uuid}_`;
        const fieldsFromForm = Object.keys(values)
          .filter(key => key.startsWith(fieldPrefix))
          .map(key => key.slice(fieldPrefix.length));

        Array.from(new Set([...Object.keys(restValues), ...fieldsFromForm])).forEach(key => {
          if (!['id', 'name', 'active'].includes(key)) {
            const valueByKey = values[`${uuid}_${key}`];
            if (valueByKey === undefined) {
              return;
            }
            res[key] = typeof valueByKey === 'object' ? valueByKey.value : valueByKey;
          }
        });
        return res;
      })
      .filter(item => !!item);

    return res;
  };

  const prepareDafaultRulesRequest = (): SiteShiftRule[] => {
    return (
      shiftRuleSchemas?.map(item => {
        return Object.keys(item).reduce((sum, curr) => {
          if (curr === 'uuid') {
            sum.id = item[curr];
            return sum;
          }
          // @ts-ignore
          sum[curr] = typeof item[curr] === 'object' ? item[curr].default : item[curr];
          return sum;
        }, {} as SiteShiftRule);
      }) ?? ([] as unknown as SiteShiftRule[])
    );
  };

  const updateShiftRules = async (needToDelete?: boolean) => {
    if (!companyId || !location?.uuid || !shift?.uuid) {
      return;
    }
    try {
      setLoadingTrue();

      await updateShiftRequest({
        companyId,
        siteId: location.uuid,
        shiftId: shift.uuid,
        shift: {
          ...shiftToSiteShift(shift),
          rules: needToDelete ? prepareDafaultRulesRequest() : prepareRulesData(),
        },
      });
      await getLocations();
      setLoadingFalse();
    } catch (e) {
      setLoadingFalse();
      handleServerError(e);
    }
  };

  const updateRulesForAllShifts = async (needToDelete?: boolean) => {
    if (!companyId) {
      return;
    }
    try {
      setLoadingTrue();

      await updateAllRulesShiftRequest({
        companyId,
        rules: { rules: needToDelete ? prepareDafaultRulesRequest() : prepareRulesData() },
      });
      await getLocations();

      setLoadingFalse();
    } catch (e) {
      setLoadingFalse();
      handleServerError(e);
    }
  };

  const handleDeleteRules = (variant: 'all' | 'selected') => {
    if (variant === 'all') {
      updateRulesForAllShifts(true);
      return;
    }
    updateShiftRules(true);
  };

  const handleChangeSelectedRules = (id: string) => {
    if (selectedRules.includes(id)) {
      setSelectedRules(values => values.filter(item => item !== id));
      return;
    }
    setSelectedRules(values => [...values, id]);
  };

  const handleSubmit = async () => {
    updateShiftRules();
  };

  const updateDefaultValues = () => {
    const newDefaultValues: Record<string, any> = {};

    for (const ruleSchema of shiftRuleSchemas ?? []) {
      const ruleFromShift = shift?.rules?.find(item => item.id === ruleSchema.uuid);
      Object.keys(ruleSchema).forEach(key => {
        if (!['uuid', 'name', 'active'].includes(key)) {
          const fieldName = `${ruleSchema.uuid}_${key}`;
          const enumOptions =
            typeof ruleSchema[key] === 'object' ? (ruleSchema[key] as unknown as EnumSchema) : null;
          const enumDefaultValue =
            enumOptions?.options.find(item => item.value === ruleFromShift?.[key]) ||
            enumOptions?.options.find(item => item.value === enumOptions.default);
          // ?? вместо ||: сохранённый 0 часов или 0 минут — валидное значение, а не «пусто»
          const valueFromShift = enumDefaultValue ?? ruleFromShift?.[key];

          const newValue = valueFromShift ?? enumDefaultValue ?? ruleSchema[key];

          newDefaultValues[fieldName] = typeof newValue === 'object' ? newValue : String(newValue);
        }
      });
    }
    Object.entries(newDefaultValues).forEach(([name, value]: any) => methods.setValue(name, value));
  };

  useEffect(() => {
    getShiftRules();
  }, []);

  useEffect(() => {
    methods.setValue('location', locations?.[0]);
  }, [locations]);

  useEffect(() => {
    if (shift && shiftRuleSchemas) {
      updateDefaultValues();
      setSelectedRules(shift.rules?.filter(item => item.active).map(item => item.id) || []);
    }
  }, [shift, shiftRuleSchemas]);

  useEffect(() => {
    if (location) {
      setShifts(location.shifts);
      methods.setValue('shift', location?.shifts?.[0]);
    }
  }, [location]);

  return {
    methods,
    locations,
    shifts,
    shiftRuleSchemas,
    selectedRules,
    isButtonDisabled,
    handleSubmit,
    handleChangeSelectedRules,
    updateRulesForAllShifts,
    handleDeleteRules,
  };
};
